import React, { ReactNode } from 'react';
import { To } from 'react-router';

import { DropdownProps } from '@/components/Dropdown';
import { NoDataProps } from '@/components/legacy-ui/NoData';
import { SortableColumnsConfig } from '@/components/table/sortableColumns';
import { type ButtonProps } from '@/components/ui/button';

export type CellAlignment = 'center' | 'left' | 'right';

/**
 * Column priority for responsive hiding.
 * Higher priority columns are hidden last (remain visible longer).
 * Lower priority columns are hidden first.
 */
export enum ColumnPriority {
  /** Hidden first: Secondary information (tags, dates, non-critical metadata) */
  LOW = 2,

  /** Hidden second: Contextual information (origins, secondary identifiers) */
  MEDIUM = 4,

  /** Hidden third: Important but not critical (status badges, attack surface) */
  MEDIUM_HIGH = 6,

  /** Hidden last: Key data columns (expandable identifier columns) */
  HIGH = 8,

  /** Never hidden: Essential UI elements (checkbox, primary identifier, actions) */
  CRITICAL = 10,
}

/**
 * Configuration for responsive column behavior.
 */
export interface ResponsiveColumnConfig {
  /**
   * Column priority for responsive hiding.
   * Lower priority columns hide first as viewport narrows.
   *
   * Use ColumnPriority enum values:
   * - ColumnPriority.CRITICAL (10) - Never hidden
   * - ColumnPriority.HIGH (8) - Hidden last
   * - ColumnPriority.MEDIUM_HIGH (6)
   * - ColumnPriority.MEDIUM (4)
   * - ColumnPriority.LOW (2) - Hidden first
   *
   * @default AUTO-INFERRED based on column properties
   * @see inferColumnPriority() in useResponsiveColumns.ts for inference logic
   *
   * @example
   * responsive: { priority: ColumnPriority.HIGH }
   */
  priority?: ColumnPriority;

  /**
   * Minimum viewport width (px) at which this column should be visible.
   * Column hidden when viewport < minViewportWidth.
   *
   * @example
   * minViewportWidth: 768  // Hide on mobile, show on tablet+
   */
  minViewportWidth?: number;

  /**
   * If true, column will never be hidden regardless of viewport width.
   * Use sparingly - only for absolutely essential columns.
   *
   * @default false
   */
  alwaysVisible?: boolean;
}

export interface TableProps<TData> {
  hideTableHeader?: boolean;
  tableClassName?: string;
  name: string;
  columns: Columns<TData>;
  data: (TData & { children?: TData[] })[];
  bodyHeader?: React.ReactElement;
  selection?: {
    value?: string[];
    onChange?: (value: string[]) => void;
    selectAll?: boolean;
    hide?: boolean;
  };
  hasNextPage?: boolean;
  sort?: {
    orderBy: keyof TData;
    descending: boolean;
    onSort: (sort: { orderBy: keyof TData; descending: boolean }) => void;
  };
  status: 'error' | 'success' | 'pending';
  error: Error | null;
  noData?: Partial<NoDataProps>;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  isFetching?: boolean;
  onRowClick?: (
    item: TData,
    rowIndex: number,
    event?: React.MouseEvent<HTMLTableRowElement>
  ) => void;
  primaryAction?: (selectedRowsData: TData[]) => ButtonProps;
  bulkActions?: (selectedRowsData: TData[]) => TableActions;
  rowActions?: (rowData: TData) => TableActions;
  loadingRowCount?: number;
  isTableView?: boolean;
  groupBy?: {
    label: string;
    filter: (data: TData) => boolean;
    icon?: ReactNode;
  }[];
  resize?: boolean;
  search?: {
    value: string;
    onChange: (value: string) => void;
  };
  rowClassName?: (rowData: TData) => string;
  /** Return extra HTML attributes for the <tr> element (e.g. aria-busy for pending rows). */
  getRowProps?: (rowData: TData) => Record<string, unknown>;
  inspecting?: number | null;
  setInspecting?: (idx: number | null) => void;
  rowHeight?: number;
  keyboardActions?: {
    codes: string[];
    onKeyboardAction: (key: string, data: TData[]) => void;
    altKey?: boolean;
  };
  keyboardNavigation?: boolean;
  sortableColumns?: {
    activeColumns: string[] | undefined;
    setActiveColumns: (columns: string[]) => void;
    defaultConfig: SortableColumnsConfig;
    placeholder?: string;
  };

  /**
   * Enable responsive column hiding.
   * When true, columns will hide based on priority as viewport narrows.
   *
   * @default true
   */
  responsive?: boolean;

  /**
   * Configuration for infinite scroll indicator behavior.
   * Controls when the scroll indicator appears and its behavior.
   *
   * @default { minItemsThreshold: 10, disabled: false }
   *
   * @example
   * ```tsx
   * // Custom threshold
   * infiniteScroll={{ minItemsThreshold: 5 }}
   *
   * // Disable indicator entirely
   * infiniteScroll={{ disabled: true }}
   * ```
   */
  infiniteScroll?: {
    /**
     * Minimum number of items required before showing the scroll indicator.
     * The indicator will only appear when itemCount >= minItemsThreshold.
     *
     * @default 10
     */
    minItemsThreshold?: number;

    /**
     * Disable the infinite scroll indicator entirely.
     * When true, the indicator will never be shown regardless of other conditions.
     *
     * @default false
     */
    disabled?: boolean;
  };

  /**
   * Maximum height for the table scroll container in pixels.
   * When set, constrains the table height while preserving virtualization.
   * Applied to the scrollableRef element that handles overflow scrolling.
   *
   * @example
   * ```tsx
   * // Constrain to approximately 9 rows (9 * 48px + header)
   * maxHeight={480}
   * ```
   */
  maxHeight?: number;

  /**
   * Minimum height for the table scroll container in pixels.
   * Prevents table from shrinking when filtering reduces rows.
   * Use with maxHeight to create stable height containers in modals.
   *
   * @example
   * ```tsx
   * // Fixed height table container (useful in modals)
   * minHeight={480}
   * maxHeight={480}
   * ```
   */
  minHeight?: number;
}

export interface Column<TData> {
  label: ReactNode;
  id: keyof TData | '';
  isLoading?: (item: TData) => boolean;
  cell?:
    | 'date'
    | 'highlight'
    | ((item: TData, selectedRowsData?: TData[]) => ReactNode);
  to?: (item: TData, selectedRowsData?: TData[]) => To;
  className?: string;
  fixedWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  isExpandable?: { minWidth: number };

  /**
   * Responsive column behavior configuration.
   * Controls when and how column hides as viewport narrows.
   *
   * @example
   * responsive: {
   *   priority: ColumnPriority.LOW,  // Hide first
   *   minViewportWidth: 768,          // Hide on mobile
   * }
   */
  responsive?: ResponsiveColumnConfig;

  /**
   * Maximum number of array items to show before "+N" counter.
   * Only applies to columns with array values using ArrayCell.
   *
   * @default 1
   * @example
   * arrayMaxVisible: 3  // Show first 3 items, then "+N"
   */
  arrayMaxVisible?: number;

  align?: CellAlignment;
  onClick?: (item: TData) => void;
  copy?: boolean;
  info?: string;
  sorting?: boolean;

  /**
   * Number of lines to allow text wrapping.
   * When set, cell content will use line-clamp-N instead of truncation.
   * Undefined = truncate (default behavior)
   *
   * @example
   * wrapLines: 2  // Allow 2 lines with ellipsis
   */
  wrapLines?: number;

  /**
   * Pin this column to the left or right edge during horizontal scroll.
   * - MUST be used together with `fixedWidth`. Dev warning emitted otherwise.
   * - Sticky columns automatically opt out of responsive column collapse.
   * - The shared Table owns stacking coordination with the sticky <thead>;
   *   consumers must NOT add their own sticky/z-index overrides via tableClassName.
   * - A shadow is rendered on the inner edge (left-shadow for 'right', right-shadow for 'left').
   *
   * @example
   * { id: 'config', label: 'Actions', sticky: 'right', fixedWidth: 150 }
   */
  sticky?: 'left' | 'right';
}

export type Columns<TData> = Column<TData>[];

export type InternalTData<TData> = TData & {
  _type?: 'colgroup';
  _label?: string;
  _idx: string;
  _key: string;
  _icon?: ReactNode;
};

export type TableActions = Pick<DropdownProps, 'menu' | 'disabled'>;

/**
 * Additional props for TableBody component.
 * Separated for clarity and reusability.
 */
export interface TableBodyHoverProps {
  /**
   * Index of the currently hovered row (lifted state from parent Table).
   * Null when no row is hovered.
   */
  hoveredRowIndex: number | null;

  /**
   * Setter for hover state (controlled by parent Table component).
   * Ensures only one row can be hovered at a time.
   */
  setHoveredRowIndex: (index: number | null) => void;
}
